<template>
  <div id="app">
    <loading ref="loading" />

    <dashboard-error-boundary>
      <transition name="page" mode="out-in">
        <component :is="layout" v-if="layout" />
      </transition>
    </dashboard-error-boundary>
    <scale-workspace-banner />
    <bootstrap-failure
      v-if="loginFailure"
      :failure="loginFailure"
      :busy="retrying"
      @retry="retryLogin"
      @sign-out="signOut"
    />
  </div>
</template>

<script>
import auth from '@groovepages/gd-lib/auth';
import Loading from './Loading';
import store from '@/state';
import Login from '../pages/auth/login.vue';
import keycloakAuth from '@/services/keycloakAuth';
import { BootstrapFailure, DashboardErrorBoundary } from './bootstrap-shell';
import ScaleWorkspaceBanner from './ScaleWorkspaceBanner.vue';

// Load layout components dynamically.
const requireContext = require.context('@/layouts', false, /.*\.vue$/);

const layouts = requireContext
  .keys()
  .map((file) => [file.replace(/(^.\/)|(\.vue$)/g, ''), requireContext(file)])
  .reduce((components, [name, component]) => {
    components[name] = component.default || component;
    return components;
  }, {});

export default {
  components: {
    Loading,
    BootstrapFailure,
    DashboardErrorBoundary,
    ScaleWorkspaceBanner,
  },

  data: () => ({
    layout: Login,
    defaultLayout: 'default',
    loginFailure: keycloakAuth.loginFailure,
    retrying: false,
  }),

  metaInfo() {
    const appName = 'Dashboard';

    return {
      title: appName,
      titleTemplate: `%s · ${appName}`,
    };
  },

  created() {
    if (!auth.authenticatedUser()) {
    } else {
      store.commit('login', auth.authenticatedUser());
    }
  },
  mounted() {
    this.$loading = this.$refs.loading;
  },

  methods: {
    /**
     * Set the application layout.
     *
     * @param {String} layout
     */
    setLayout(layout) {
      if (!layout || !layouts[layout]) {
        layout = this.defaultLayout;
      }

      this.layout = layouts[layout];
    },

    showLoginFailure(failure) {
      this.loginFailure = failure || keycloakAuth.loginFailure;
    },

    async retryLogin() {
      if (this.retrying) return;
      this.retrying = true;
      try {
        const user = await keycloakAuth.loadCompleteUserProfile();
        if (user && user.id != null && user.id !== '') {
          this.$store.commit('login', user);
          window.location.reload();
          return;
        }
        this.loginFailure = keycloakAuth.loginFailure;
      } finally {
        this.retrying = false;
      }
    },

    signOut() {
      return keycloakAuth.logout();
    },
  },
};
</script>

<style>
.gd-bootstrap-failure,
.gd-dashboard-error {
  box-sizing: border-box;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #fafbfe;
  color: #202a3d;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.gd-bootstrap-failure {
  position: fixed;
  inset: 0;
  z-index: 10000;
}
.gd-bootstrap-failure__card,
.gd-dashboard-error__card {
  width: 100%;
  max-width: 440px;
}
.gd-bootstrap-failure h1,
.gd-dashboard-error h1 {
  font-size: 1.5rem;
  line-height: 1.3;
  margin: 0 0 12px;
}
.gd-bootstrap-failure p,
.gd-dashboard-error p {
  margin: 0 0 12px;
  line-height: 1.5;
  color: #637087;
}
.gd-bootstrap-failure a {
  color: #f72485;
}
.gd-bootstrap-failure button,
.gd-dashboard-error button {
  background: #f72485;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 10px 16px;
  margin: 8px 8px 0 0;
  cursor: pointer;
  font-weight: 650;
}
.gd-bootstrap-failure button[disabled] {
  opacity: 0.6;
  cursor: default;
}
.gd-bootstrap-failure__actions button:last-child {
  background: #fff;
  color: #202a3d;
  border: 1px solid #cccedc;
}
</style>
